import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { ProjectEventType } from "@domain/project/project-event";
import type { ScopedProject } from "@domain/project/project-guards";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import { Expose, Transform, Type } from "class-transformer";
import { IsEnum, IsOptional, IsString, ValidateNested } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class TelemetryConfiguration extends ProductConfiguration {
  @IsString()
  public familyNumber: string;

  @IsEnum(MonitoringCategory)
  @Expose({ name: "category" })
  private _category!: MonitoringCategory;

  @Type(() => MonitoringProductItem)
  @Transform(({ value }) => (value === undefined || value === null || Array.isArray(value) ? value : [value]), {
    toClassOnly: true,
  })
  @IsOptional()
  @Expose({ name: "family" })
  @ValidateNested({ each: true })
  private _family?: MonitoringProductItem[];

  @IsOptional()
  @Type(() => MonitoringProductItem)
  @Expose({ name: "sensor" })
  @ValidateNested()
  private _sensor?: MonitoringProductItem;

  @IsOptional()
  @Type(() => MonitoringProductItem)
  @ValidateNested({ each: true })
  @Expose({ name: "additionalBatteries" })
  private _additionalBatteries?: MonitoringProductItem[];

  constructor(
    id: string,
    name: string,
    project: ScopedProject,
    familyNumber: string,
    category: MonitoringCategory,
    family: MonitoringProductItem[] | undefined,
    sensor?: MonitoringProductItem,
    additionalBatteries?: MonitoringProductItem[],
  ) {
    super(id, name, project);
    this.familyNumber = familyNumber;
    this.category = category;
    this.family = family;
    this.sensor = sensor;
    this.additionalBatteries = additionalBatteries;
  }

  static create(
    name: string,
    project: ScopedProject,
    familyNumber: string,
    category: MonitoringCategory,
    family: MonitoringProductItem[] | undefined,
    sensor?: MonitoringProductItem,
    additionalBatteries?: MonitoringProductItem[],
  ): TelemetryConfiguration {
    return new TelemetryConfiguration(uuidv4(), name, project, familyNumber, category, family, sensor, additionalBatteries);
  }

  update(
    familyName: string,
    familyNumber: string,
    family: MonitoringProductItem[] | undefined,
    sensor?: MonitoringProductItem,
    additionalBatteries?: MonitoringProductItem[],
  ) {
    this.name = familyName;
    this.familyNumber = familyNumber;
    this.family = family;
    this.sensor = sensor;
    this.additionalBatteries = additionalBatteries;
    this.project.publishUpdate(ProjectEventType.TELEMETRY_CONFIGURATION_UPDATED, this);
  }

  getDeviceMatNr() {
    const matNr: string[] = [
      ...this.familyVariantsDevices,
      ...(this._sensor?.option ? this._sensor.option.devices : []),
      ...this.additionalBatteriesDevices,
    ];
    return matNr;
  }

  override get mainProductId(): string {
    return this.familyNumber;
  }

  get category(): MonitoringCategory {
    return this._category;
  }

  get family(): MonitoringProductItem[] | undefined {
    return this._family;
  }

  get sensor(): MonitoringProductItem | undefined {
    return this._sensor;
  }

  get additionalBatteries(): MonitoringProductItem[] | undefined {
    return this._additionalBatteries;
  }

  set category(value: MonitoringCategory) {
    this._category = value;
  }

  set family(value: MonitoringProductItem[] | undefined) {
    this._family = value;
  }

  set sensor(value: MonitoringProductItem | undefined) {
    this._sensor = value;
  }

  set additionalBatteries(value: MonitoringProductItem[] | undefined) {
    this._additionalBatteries = value;
  }

  get configuredProductIds(): string[] {
    return [
      ...this.familyVariantsDevices,
      ...(this.sensor?.option ? this.sensor.option.devices : []),
      ...this.additionalBatteriesDevices,
    ];
  }

  getOptions(): MonitoringProductItem[] {
    return [
      ...(this.family ?? []),
      ...(this.sensor?.option ? [this.sensor] : []),
      ...(this.additionalBatteries ? this.additionalBatteries : []),
    ];
  }

  getAllItems(): MonitoringProductItem[] {
    return this.getOptions();
  }

  private get familyVariantsDevices(): string[] {
    return this.family?.flatMap((familyItem) => familyItem.option.devices) ?? [];
  }

  get additionalBatteriesDevices(): string[] {
    return this.additionalBatteries?.flatMap((additionalBattery) => additionalBattery.option.devices) ?? [];
  }

  countPlacedProducts(project: ScopedProject = this.project): number {
    return project ? project.countPlacedTelemetries(this) : 0;
  }
}
